export const HUBSPOT_CHAT_STATE_EVENT = 'ly:hubspot-chat:state-change'
export const VIDEO_APPOINTMENT_STATE_EVENT = 'ly:video-appointment:state-change'

export function resolveWidgetAction(status) {
  return status?.loaded ? 'ready' : 'load'
}

export function appendReadyCallback(windowRef, callback) {
  const callbacks = Array.isArray(windowRef.hsConversationsOnReady)
    ? windowRef.hsConversationsOnReady
    : []

  if (!callbacks.includes(callback)) callbacks.push(callback)

  windowRef.hsConversationsOnReady = callbacks
}

export function shouldLockDocumentScroll(viewportWidth, open) {
  return Boolean(open && Number(viewportWidth) < 768)
}

export function secondsToMilliseconds(seconds, fallbackSeconds = 12) {
  const value = Number(seconds)
  return (Number.isFinite(value) ? value : fallbackSeconds) * 1000
}

export function ensureHubSpotTrackingScript(documentRef, options = {}) {
  const existing = documentRef?.getElementById?.('hs-script-loader')

  if (existing) return existing

  const portalId = String(options.portalId || '').trim()
  const region = String(options.region || 'na2').trim().toLowerCase()

  if (!/^\d+$/.test(portalId) || !/^[a-z0-9-]+$/.test(region)) {
    throw new Error('A valid HubSpot portal ID and region are required')
  }

  const script = documentRef.createElement('script')

  script.id = 'hs-script-loader'
  script.src = `https://js-${region}.hs-scripts.com/${portalId}.js`
  script.async = true
  script.defer = true
  script.dataset.lyHubspotTracking = 'true'

  documentRef.head.append(script)

  return script
}

export function createHubSpotWidgetLoader(windowRef, options = {}) {
  const timeoutMs = Math.max(
    1000,
    Number(options.timeoutMs) || 12000,
  )

  const setTimer =
    options.setTimer ||
    windowRef.setTimeout?.bind(windowRef) ||
    setTimeout

  const clearTimer =
    options.clearTimer ||
    windowRef.clearTimeout?.bind(windowRef) ||
    clearTimeout

  const documentRef = options.documentRef || windowRef.document

  let pending = null
  let readyCallbackRegistered = false
  let widgetEventRegistered = false
  let resolvedWidget = null

  const finish = (attempt, error, widget) => {
    if (!attempt || pending !== attempt || attempt.settled) return

    attempt.settled = true
    clearTimer(attempt.timer)
    pending = null

    if (error) {
      attempt.reject(error)
      return
    }

    resolvedWidget = widget
    attempt.resolve(widget)
  }

  const handleWidgetLoaded = () => {
    const widget = windowRef.HubSpotConversations?.widget

    if (widget) finish(pending, null, widget)
  }

  const startPendingAttempt = () => {
    const attempt = pending

    if (!attempt || attempt.settled || attempt.started) return

    const conversations = windowRef.HubSpotConversations
    const widget = conversations?.widget

    if (!widget) return

    attempt.started = true

    try {
      if (
        !widgetEventRegistered &&
        typeof conversations.on === 'function'
      ) {
        conversations.on('widgetLoaded', handleWidgetLoaded)
        widgetEventRegistered = true
      }

      const action = resolveWidgetAction(widget.status?.())

      if (action === 'ready') {
        finish(attempt, null, widget)
        return
      }

      widget.load()
    } catch (error) {
      finish(attempt, error)
    }
  }

  const handleConversationsReady = () => {
    startPendingAttempt()
  }

  const getReadyWidget = () => {
    const widget =
      resolvedWidget ||
      windowRef.HubSpotConversations?.widget

    if (
      !widget ||
      resolveWidgetAction(widget.status?.()) !== 'ready'
    ) {
      return null
    }

    resolvedWidget = widget

    return widget
  }

  return {
    isReady() {
      return Boolean(getReadyWidget())
    },

    ensure() {
      const readyWidget = getReadyWidget()

      if (readyWidget) {
        return Promise.resolve(readyWidget)
      }

      if (pending) return pending.promise

      let resolve
      let reject

      const promise = new Promise(
        (resolvePromise, rejectPromise) => {
          resolve = resolvePromise
          reject = rejectPromise
        },
      )

      const attempt = {
        promise,
        resolve,
        reject,
        settled: false,
        started: false,
        timer: null,
      }

      attempt.timer = setTimer(() => {
        finish(
          attempt,
          new Error(
            `HubSpot Conversations did not become ready within ${timeoutMs}ms`,
          ),
        )
      }, timeoutMs)

      pending = attempt

      if (!readyCallbackRegistered) {
        appendReadyCallback(
          windowRef,
          handleConversationsReady,
        )

        readyCallbackRegistered = true
      }

      try {
        if (
          !windowRef.HubSpotConversations &&
          options.portalId
        ) {
          ensureHubSpotTrackingScript(documentRef, {
            portalId: options.portalId,
            region: options.region,
          })
        }
      } catch (error) {
        finish(attempt, error)
        return promise
      }

      startPendingAttempt()

      return promise
    },
  }
}

const HubSpotChatElement =
  typeof HTMLElement === 'undefined'
    ? class {}
    : HTMLElement

export class LyHubSpotLiveChat extends HubSpotChatElement {
  connectedCallback() {
    this.launcher = this.querySelector(
      '[data-hubspot-chat-launcher]',
    )

    this.dialog = this.querySelector(
      '[data-hubspot-chat-dialog]',
    )

    this.closeButton = this.querySelector(
      '[data-hubspot-chat-close]',
    )

    this.loading = this.querySelector(
      '[data-hubspot-chat-loading]',
    )

    this.offline = this.querySelector(
      '[data-hubspot-chat-offline]',
    )

    this.offlineTitle = this.querySelector(
      '[data-hubspot-offline-title]',
    )

    this.offlineFormFields = this.querySelector(
      '[data-hubspot-offline-form]',
    )

    this.offlineFormError = this.querySelector(
      '[data-hubspot-offline-error]',
    )

    this.offlineFormSuccess = this.querySelector(
      '[data-hubspot-offline-success]',
    )

    this.embed = this.querySelector(
      '[data-hubspot-chat-embed]',
    )

    this.handleClick = this.handleClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleWindowLoad = this.handleWindowLoad.bind(this)

    this.handleVideoAppointmentState =
      this.handleVideoAppointmentState.bind(this)

    this.loader = createHubSpotWidgetLoader(window, {
      documentRef: document,
      portalId: this.dataset.portalId,
      region: this.dataset.hubspotRegion,
      timeoutMs: secondsToMilliseconds(
        this.dataset.sdkTimeout,
      ),
    })

    this.state = 'closed'
    this.videoAppointmentOpen = false
    this.prewarmHandle = null
    this.prewarmMode = null
    this.prewarmWaitingForLoad = false

    this.addEventListener('click', this.handleClick)

    document.addEventListener(
      'keydown',
      this.handleKeydown,
    )

    document.addEventListener(
      VIDEO_APPOINTMENT_STATE_EVENT,
      this.handleVideoAppointmentState,
    )

    window.addEventListener(
      'resize',
      this.handleResize,
      { passive: true },
    )

    if (
      this.offlineFormFields?.dataset
        .postedSuccessfully === 'true'
    ) {
      this.setOfflineFormState('success')
      this.setState('offline')
      this.emitOpenState(true)
    } else if (
      this.offlineFormFields?.dataset
        .hasErrors === 'true'
    ) {
      this.setOfflineFormState('error')
      this.setState('offline')
      this.emitOpenState(true)
      this.offlineFormError?.focus()
    } else {
      this.setState('closed')
    }

    this.schedulePrewarm()
  }

  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.handleClick,
    )

    document.removeEventListener(
      'keydown',
      this.handleKeydown,
    )

    document.removeEventListener(
      VIDEO_APPOINTMENT_STATE_EVENT,
      this.handleVideoAppointmentState,
    )

    window.removeEventListener(
      'resize',
      this.handleResize,
    )

    this.cancelPrewarm()

    document.documentElement.classList.remove(
      'ly-hubspot-live-chat-open',
    )
  }

  handleClick(event) {
    if (
      event.target.closest(
        '[data-hubspot-chat-launcher]',
      )
    ) {
      this.open()
    }

    if (
      event.target.closest(
        '[data-hubspot-chat-close]',
      )
    ) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (
      event.key !== 'Escape' ||
      this.state === 'closed'
    ) {
      return
    }

    event.preventDefault()
    this.close()
  }

  handleResize() {
    this.syncDocumentScroll()
  }

  handleVideoAppointmentState(event) {
    this.videoAppointmentOpen = Boolean(
      event.detail?.open,
    )

    if (
      this.videoAppointmentOpen &&
      this.state !== 'closed'
    ) {
      this.close({ restoreFocus: false })
      return
    }

    this.syncLauncherVisibility()
  }

  open() {
    if (
      !this.dialog ||
      !this.launcher ||
      this.videoAppointmentOpen
    ) {
      return
    }

    this.cancelPrewarm()

    const widgetReady = Boolean(
      this.loader?.isReady?.(),
    )

    this.setState(
      widgetReady ? 'ready' : 'loading',
    )

    this.emitOpenState(true)
    this.closeButton?.focus()

    if (!widgetReady) {
      this.ensureWidget()
    }
  }

  close({ restoreFocus = true } = {}) {
    if (this.state === 'closed') return

    this.setState('closed')
    this.emitOpenState(false)

    if (
      restoreFocus &&
      !this.launcher?.hidden
    ) {
      this.launcher.focus()
    }
  }

  ensureWidget() {
    return this.loader
      .ensure()
      .then(() => {
        if (this.state !== 'closed') {
          this.setState('ready')
        }
      })
      .catch(() => {
        if (this.state !== 'closed') {
          this.setState('offline')
          this.offlineTitle?.focus()
        }
      })
  }

  handleWindowLoad() {
    this.prewarmWaitingForLoad = false
    this.queueIdlePrewarm()
  }

  schedulePrewarm() {
    if (document.readyState === 'complete') {
      this.queueIdlePrewarm()
      return
    }

    this.prewarmWaitingForLoad = true

    window.addEventListener(
      'load',
      this.handleWindowLoad,
      { once: true },
    )
  }

  queueIdlePrewarm() {
    const prewarm = () => {
      this.prewarmHandle = null
      this.prewarmMode = null

      this.loader.ensure().catch(() => {})
    }

    if (
      typeof window.requestIdleCallback ===
      'function'
    ) {
      this.prewarmMode = 'idle'

      this.prewarmHandle =
        window.requestIdleCallback(
          prewarm,
          { timeout: 4000 },
        )

      return
    }

    this.prewarmMode = 'timeout'

    this.prewarmHandle = window.setTimeout(
      prewarm,
      1500,
    )
  }

  cancelPrewarm() {
    if (this.prewarmWaitingForLoad) {
      window.removeEventListener(
        'load',
        this.handleWindowLoad,
      )

      this.prewarmWaitingForLoad = false
    }

    if (this.prewarmHandle == null) return

    if (this.prewarmMode === 'idle') {
      window.cancelIdleCallback?.(
        this.prewarmHandle,
      )
    } else {
      window.clearTimeout(
        this.prewarmHandle,
      )
    }

    this.prewarmHandle = null
    this.prewarmMode = null
  }

  setOfflineFormState(state) {
    const success = state === 'success'
    const error = state === 'error'

    if (this.offlineFormFields) {
      this.offlineFormFields.hidden = success
    }

    if (this.offlineFormSuccess) {
      this.offlineFormSuccess.hidden = !success
    }

    if (this.offlineFormError) {
      this.offlineFormError.hidden = !error
    }
  }

  emitOpenState(open) {
    document.dispatchEvent(
      new CustomEvent(
        HUBSPOT_CHAT_STATE_EVENT,
        {
          detail: {
            open: Boolean(open),
          },
        },
      ),
    )
  }

  setState(state) {
    this.state = state

    const open = state !== 'closed'

    this.classList.toggle(
      'is-open',
      open,
    )

    this.classList.toggle(
      'is-loading',
      state === 'loading',
    )

    this.classList.toggle(
      'is-ready',
      state === 'ready',
    )

    this.classList.toggle(
      'is-offline',
      state === 'offline',
    )

    if (this.dialog) {
      this.dialog.hidden = !open
    }

    if (this.launcher) {
      this.launcher.setAttribute(
        'aria-expanded',
        open ? 'true' : 'false',
      )
    }

    if (this.loading) {
      this.loading.hidden =
        state !== 'loading'
    }

    if (this.offline) {
      this.offline.hidden =
        state !== 'offline'
    }

    if (this.embed) {
      this.embed.hidden =
        state === 'offline'
    }

    this.syncLauncherVisibility()
    this.syncDocumentScroll()
  }

  syncLauncherVisibility() {
    if (!this.launcher) return

    this.launcher.hidden =
      this.state !== 'closed' ||
      this.videoAppointmentOpen
  }

  syncDocumentScroll() {
    document.documentElement.classList.toggle(
      'ly-hubspot-live-chat-open',
      shouldLockDocumentScroll(
        window.innerWidth,
        this.state !== 'closed',
      ),
    )
  }
}

if (
  typeof window !== 'undefined' &&
  !customElements.get('ly-hubspot-live-chat')
) {
  customElements.define(
    'ly-hubspot-live-chat',
    LyHubSpotLiveChat,
  )
}